import React from "react";
import { Box, Stack, Tooltip } from "@mui/material";
import { tokens } from "../../theme";
import { toneColors } from "../statusTone";

/**
 * A month of team coverage in one row: each cell is a day, showing how many
 * people are away. Same geometry and interaction as the attendance MonthStrip,
 * so the two surfaces read as one system — but here the cell carries a
 * headcount, and its colour is how close that day is to leaving the team short.
 */

const summarise = (day) => {
  if (!day.count) return "Nobody away";
  const names = day.people.map((p) => {
    const half = p.window !== "full-day" ? ` (${p.window.replace("-", " ")})` : "";
    return `${p.employee} — ${p.leaveType}${half}`;
  });
  // a long list in a tooltip stops being readable, so cap it
  const shown = names.slice(0, 6);
  if (names.length > shown.length) shown.push(`+${names.length - shown.length} more`);
  return shown.join("\n");
};

const CoverageStrip = ({ days = [], selectedDay, onSelectDay }) => (
  <Box sx={{ overflowX: "auto", pb: 0.5 }}>
    <Stack direction="row" spacing={0.25} sx={{ minWidth: "min-content", alignItems: "stretch" }}>
      {days.map((d) => {
        const { fg, bg } = toneColors(d.tone);
        const isSelected = selectedDay === d.dayString;
        const isEmpty = d.count === 0;
        const startsWeek = d.weekday === "Sun" && d.dayNum !== 1;

        return (
          <Tooltip
            key={d.dayString}
            arrow
            title={
              <Box sx={{ whiteSpace: "pre-line", fontSize: "0.8125rem" }}>
                {`${d.weekday} ${d.dayNum}\n${summarise(d)}`}
              </Box>
            }
          >
            <Box
              role="button"
              tabIndex={isEmpty ? -1 : 0}
              aria-pressed={isSelected}
              aria-label={`${d.weekday} ${d.dayNum}, ${d.count} away`}
              onClick={() => !isEmpty && onSelectDay?.(isSelected ? null : d.dayString)}
              onKeyDown={(e) => {
                if (isEmpty || (e.key !== "Enter" && e.key !== " ")) return;
                e.preventDefault();
                onSelectDay?.(isSelected ? null : d.dayString);
              }}
              sx={{
                flex: "1 1 0",
                minWidth: "2.25rem",
                textAlign: "center",
                cursor: isEmpty ? "default" : "pointer",
                userSelect: "none",
                ml: startsWeek ? "0.625rem" : 0,
                borderRadius: "8px",
                "&:focus-visible": { outline: `2px solid ${tokens.accent}`, outlineOffset: "2px" },
              }}
            >
              <Box
                sx={{
                  fontSize: "0.625rem",
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  color: tokens.text,
                  opacity: 0.75,
                }}
              >
                {d.weekday.charAt(0)}
              </Box>
              <Box
                sx={{
                  fontSize: "0.6875rem",
                  fontWeight: d.isToday ? 700 : 500,
                  fontVariantNumeric: "tabular-nums",
                  color: d.isToday ? tokens.text : tokens.textMuted,
                  mb: 0.5,
                }}
              >
                {d.dayNum}
              </Box>
              <Box
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  height: "1.875rem",
                  // Centred by the box rather than by line-height: the cell is
                  // much taller than its text line, so relying on the line box
                  // left every digit sitting against the top edge with the rest
                  // of the cell empty underneath it.
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1,
                  borderRadius: "6px",
                  fontVariantNumeric: "tabular-nums",
                  color: isEmpty ? tokens.border : fg,
                  background: isEmpty ? "transparent" : bg,
                  border: isEmpty ? `1px dashed ${tokens.border}` : `1px solid ${isSelected ? fg : "transparent"}`,
                  boxShadow: isSelected ? `0 0 0 2px ${bg}, 0 0 0 3px ${fg}` : "none",
                  opacity: d.isFuture && isEmpty ? 0.6 : 1,
                  transition: "box-shadow 120ms cubic-bezier(0.2, 0, 0.2, 1), border-color 120ms",
                }}
              >
                {isEmpty ? "·" : d.count}
              </Box>
              <Box
                aria-hidden
                sx={{
                  height: "2px",
                  mt: 0.5,
                  mx: "auto",
                  width: d.isToday ? "1.25rem" : "100%",
                  borderRadius: "6px",
                  background: d.isToday ? tokens.text : "transparent",
                }}
              />
            </Box>
          </Tooltip>
        );
      })}
    </Stack>
  </Box>
);

/**
 * The coverage thresholds.
 *
 * Outlined circles rather than filled swatches, which the book gives a reason
 * for: "a day is either flagged or it is not." A filled chip reads as a
 * quantity — a shade of severity — when what it actually marks is a line the
 * day has or has not crossed.
 */
export const CoverageLegend = ({ headcount }) => {
  const items = [
    { tone: "info", label: "Some cover lost" },
    { tone: "warning", label: headcount ? "20% or more of the team away" : "3 or more away" },
    { tone: "danger", label: headcount ? "A third of the team away" : "5 or more away" },
  ];
  return (
    <Stack
      direction="row"
      sx={{ gap: "22px", flexWrap: "wrap", mt: "18px", pt: "16px", borderTop: `1px solid ${tokens.border}` }}
    >
      {items.map(({ tone, label }) => (
        <Stack key={tone} direction="row" alignItems="center" spacing={0.625}>
          <Box
            aria-hidden="true"
            sx={{
              width: "0.75rem",
              height: "0.75rem",
              borderRadius: "50%",
              boxSizing: "border-box",
              background: "transparent",
              border: `2px solid ${toneColors(tone).fg}`,
            }}
          />
          <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted }}>{label}</Box>
        </Stack>
      ))}
    </Stack>
  );
};

export default CoverageStrip;
